import {
	type AddCartItemParams,
	type Cart,
	getCartItemQuantityIncrementStep,
	PRICES,
} from "@dransay/api/models";
import * as Sentry from "@sentry/react-router";
import type React from "react";
import {
	createContext,
	type PropsWithChildren,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { useCurrency } from "~/hooks/use-currency-price.hook";
import { useDisclosure } from "~/hooks/use-disclosure";
import { useError } from "~/hooks/use-error.hook";
import { isExpectedCartError } from "~/routes/helpers/is-expected-cart-error";
import { captureEvent, trackEvent } from "~/tooling/telemetry/capture-event";
import {
	cartEmptiedEvent,
	productAddedToCartEvent,
	productRemovedFromCartEvent,
} from "~/tooling/telemetry/events";
import { getAccessoryItemIds } from "./accessory-lines";
import type {
	CartContextType,
	Item,
	ItemUpdatePayload,
} from "./cart.context.types";
import { useCartOptimisticUpdate } from "./use-cart-optimistic-update";

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<PropsWithChildren<{ cart: Cart }>> = ({
	children,
	cart: initialCart,
}) => {
	const currency = useCurrency();

	const recentlyAddedItem = useRef<Item | null>(null);
	const recentlyRemovedItem = useRef<Item | null>(null);
	const { showError, hideError } = useError();
	const { t } = useTranslation();

	const onError = useCallback(
		(error: Error) => {
			// Only report unexpected errors to Sentry, not business validation errors
			if (!isExpectedCartError(error)) {
				Sentry.captureException(error, {
					extra: {
						context: "cart-operation",
						cartId: initialCart.cartId,
					},
				});
			}

			// Always show errors to the user
			showError({
				message: error.message.toString(),
				telemetry: {
					category: isExpectedCartError(error) ? "validation" : "technical",
				},
				primaryButton: {
					action: hideError,
					label: t("close"),
				},
			});
		},
		[showError, hideError, t, initialCart.cartId],
	);

	const {
		cart,
		updateLocalItemQuantity,
		submit,
		syncCartFromUpstream,
		isLoading,
		error,
	} = useCartOptimisticUpdate(initialCart, { onError });

	const getCartItemQuantity = useCallback(
		(itemId: string): number => {
			const item = cart.items.find((item) => item.id === itemId);
			return item ? item.quantity : 0;
		},
		[cart],
	);

	const trackItemAddition = useCallback(
		(item: Item) => {
			const currentItem = cart.items.find((ci) => ci.id === item.id);

			if (!currentItem) return false;

			trackEvent("add_to_cart", {
				cart,
				itemId: item.id,
				prescriptionValue: PRICES.onlineService,
			});

			captureEvent(
				productAddedToCartEvent({
					cartId: cart.cartId,
					quantity: item.quantity,
					currency,
					product: currentItem.product,
					productVendor: cart.vendor,
					cartItems: cart.items,
				}),
			);

			return true;
		},
		[cart, currency],
	);

	const trackItemRemoval = useCallback(
		(item: Item) => {
			const currentItem = cart.items.find((ci) => ci.id === item.id);

			trackEvent("remove_from_cart", {
				cart,
				itemId: item.id,
				prescriptionValue: PRICES.onlineService,
			});

			if (!currentItem) {
				return false;
			}

			captureEvent(
				productRemovedFromCartEvent({
					cartId: cart.cartId,
					quantity: item.quantity,
					currency,
					product: currentItem.product,
					productVendor: cart.vendor,
					cartItems: cart.items.filter((ci) => ci.quantity > 0),
				}),
			);

			return true;
		},
		[cart, currency],
	);

	const increaseCartItemQuantity = useCallback(
		(originalParams: AddCartItemParams) => {
			const itemId = originalParams.item.id;
			const increment = originalParams.item.quantity;
			const quantity = getCartItemQuantity(originalParams.item.id) + increment;

			const addItemParams = {
				...originalParams,
				item: { ...originalParams.item, quantity },
			};

			if (quantity < 0) {
				throw new Error("Cannot decrease quantity below zero");
			}

			updateLocalItemQuantity(itemId, quantity);

			const promise = submit(
				{ body: JSON.stringify({ ...addItemParams }) },
				{
					action: "/api/cart/add",
					method: "POST",
				},
			);
			recentlyAddedItem.current = addItemParams.item;

			return promise;
		},
		[submit, getCartItemQuantity, updateLocalItemQuantity],
	);

	const addToCart = useCallback(
		(addItemParams: AddCartItemParams) => {
			const isSameVendor = cart.vendor?.id === addItemParams.vendorId;
			const productAlreadyInCart = cart.items.some(
				(item) => item.id === addItemParams.item.id,
			);
			if (isSameVendor && productAlreadyInCart) {
				// If the product is already in the cart, just increase its quantity
				return increaseCartItemQuantity(addItemParams);
			}

			const promise = submit(
				{ body: JSON.stringify({ ...addItemParams }) },
				{
					action: "/api/cart/add",
					method: "POST",
				},
			);
			recentlyAddedItem.current = addItemParams.item;

			return promise;
		},
		[submit, cart, increaseCartItemQuantity],
	);

	useEffect(() => {
		if (recentlyAddedItem.current) {
			trackItemAddition(recentlyAddedItem.current);
		}

		if (recentlyRemovedItem.current) {
			trackItemRemoval(recentlyRemovedItem.current);
		}

		recentlyAddedItem.current = null;
		recentlyRemovedItem.current = null;
	}, [trackItemAddition, trackItemRemoval]);

	useCartEmptiedTracking(cart.cartId, cart.items);

	// @todo: add analytics once this feature is re-introduced
	const updateCartItem = useCallback(
		(item: ItemUpdatePayload) => {
			return submit(item, {
				action: "/api/cart/update-item",
				method: "POST",
			});
		},
		[submit],
	);

	const removeFromCart = useCallback(
		(item: Item) => {
			recentlyRemovedItem.current = item;

			return submit(item, {
				action: "/api/cart/remove",
				method: "POST",
			});
		},
		[submit],
	);

	const decreaseCartItemQuantity = useCallback(
		(item: Item) => {
			const decrementStep = item.quantity;
			if (!Number.isFinite(decrementStep) || decrementStep <= 0) {
				return Promise.resolve();
			}
			const currentQuantity = getCartItemQuantity(item.id);
			if (!Number.isFinite(currentQuantity)) {
				return Promise.resolve();
			}
			const quantity = Math.max(0, currentQuantity - decrementStep);
			updateLocalItemQuantity(item.id, quantity);
			recentlyRemovedItem.current = item;
			return submit(
				{ id: item.id, quantity },
				{
					action: "/api/cart/remove",
					method: "POST",
				},
			);
		},
		[submit, getCartItemQuantity, updateLocalItemQuantity],
	);

	const updateDeliveryMethod = useCallback(
		(deliveryMethodUuid: string) => {
			return submit(
				{ deliveryMethodUuid },
				{
					action: "/api/cart/update",
					method: "POST",
				},
			);
		},
		[submit],
	);

	const clearCart = useCallback(() => {
		const promise = submit(
			{},
			{
				action: "/api/cart/clear",
				method: "POST",
			},
		);

		cart.items.forEach((ci) => {
			captureEvent(
				productRemovedFromCartEvent({
					cartId: cart.cartId,
					quantity: ci.quantity,
					currency,
					product: ci.product,
					productVendor: cart.vendor,
					cartItems: [],
				}),
			);
		});

		return promise;
	}, [submit, cart, currency]);

	const cartDisclosure = useDisclosure(false);

	const getIncrementStep = useCallback(
		(category?: string | null, packageSize?: number | null) =>
			getCartItemQuantityIncrementStep({ category, packageSize }),
		[],
	);

	// Accessories belong to the OTC checkout journey; the marketplace cart view must not list them.
	const pharmacyItems = useMemo(() => {
		const crossSellingIds = getAccessoryItemIds(cart.fulfillmentGroups);
		return crossSellingIds.size === 0
			? cart.items
			: cart.items.filter((item) => !crossSellingIds.has(item.id));
	}, [cart.fulfillmentGroups, cart.items]);

	const context = useMemo(
		() => ({
			stockValidation: cart?.stockValidation,
			items: cart.items,
			pharmacyItems: pharmacyItems,
			vendor: cart.vendor,
			cartId: cart.cartId,
			deliveryMethodUuid: cart.deliveryMethodUuid,
			fulfillmentGroups: cart.fulfillmentGroups,
			maxTotalCartQuantity: cart.maxTotalCartQuantity,
			remainingCapacity: cart.remainingCapacity,
			remainingItemsLimit: cart.remainingItemsLimit,
			remainingAccessoryQuantity: cart.remainingAccessoryQuantity,
			totalPrice: pharmacyItems.reduce(
				(acc, item) => acc + (item.product.price ?? 0) * item.quantity,
				0,
			),
			totalQuantity: pharmacyItems.reduce(
				(acc, item) => acc + item.quantity,
				0,
			),
			getIncrementStep: getIncrementStep,
			getCartItemQuantity,
			addToCart,
			updateCartItem,
			removeFromCart,
			clearCart,
			updateDeliveryMethod,
			isLoading,
			error,
			cartDisclosure,
			increaseCartItemQuantity,
			decreaseCartItemQuantity,
			applyServerCart: syncCartFromUpstream,
		}),
		[
			cart,
			pharmacyItems,
			addToCart,
			removeFromCart,
			clearCart,
			updateCartItem,
			updateDeliveryMethod,
			getIncrementStep,
			getCartItemQuantity,
			isLoading,
			error,
			cartDisclosure,
			increaseCartItemQuantity,
			decreaseCartItemQuantity,
			syncCartFromUpstream,
		],
	);

	return (
		<CartContext.Provider value={context}>{children}</CartContext.Provider>
	);
};

export const useOptionalCart = () => useContext(CartContext);

export const useCart = (): CartContextType => {
	const context = useOptionalCart();

	if (context === undefined) {
		throw new Error("useCart must be used within a CartProvider");
	}

	return context;
};

const useCartEmptiedTracking = (
	cartId: Cart["cartId"],
	items: Cart["items"],
) => {
	const cartIdRef = useRef(cartId);
	const cartItemsLengthRef = useRef(items.length);

	useEffect(() => {
		if (cartItemsLengthRef.current > 0 && items.length === 0) {
			captureEvent(cartEmptiedEvent(cartIdRef.current));
		}

		cartItemsLengthRef.current = items.length;
		cartIdRef.current = cartId;
	}, [items.length, cartId]);
};
